import type { Col } from "../art/pal.ts";
import { SCREEN_H, SCREEN_W, type Gfx } from "../engine/gfx.ts";
import { fbm, hash2 } from "./noise.ts";

/** Day length in real milliseconds of play (one in-game day = 24 minutes). */
export const DAY_MS = 24 * 60 * 1000;

export interface DayLight {
  /** Hour 0..24. */
  hour: number;
  /** Multiplicative ambient. */
  amb: [number, number, number];
  /** 0 = full day, 1 = deep night. */
  night: number;
  /** Warm golden-hour amount 0..1. */
  golden: number;
  label: string;
}

const KEYS: { h: number; amb: [number, number, number] }[] = [
  { h: 0, amb: [0.34, 0.4, 0.66] },
  { h: 4.5, amb: [0.36, 0.42, 0.68] },
  { h: 6, amb: [0.86, 0.72, 0.78] },
  { h: 7.5, amb: [1.02, 0.98, 0.94] },
  { h: 12, amb: [1.06, 1.05, 1.0] },
  { h: 16.5, amb: [1.06, 1.0, 0.9] },
  { h: 18.5, amb: [1.12, 0.84, 0.66] },
  { h: 19.6, amb: [0.66, 0.56, 0.78] },
  { h: 21, amb: [0.38, 0.43, 0.7] },
  { h: 24, amb: [0.34, 0.4, 0.66] },
];

export function hourFor(playMs: number, override?: string | null): number {
  if (override === "night") return 22.5;
  if (override === "dusk" || override === "evening") return 18.6;
  if (override === "dawn" || override === "morning") return 6.6;
  if (override === "noon" || override === "day") return 11;
  if (override && /^\d+(\.\d+)?$/.test(override)) return Number(override) % 24;
  // Start the first session at 9:00 so the opening walk is in daylight.
  return (9 + (playMs / DAY_MS) * 24) % 24;
}

export function dayLight(hour: number): DayLight {
  let a = KEYS[0]!, b = KEYS[KEYS.length - 1]!;
  for (let i = 0; i < KEYS.length - 1; i++) {
    if (hour >= KEYS[i]!.h && hour <= KEYS[i + 1]!.h) { a = KEYS[i]!; b = KEYS[i + 1]!; break; }
  }
  const t = (hour - a.h) / Math.max(0.0001, b.h - a.h);
  const amb: [number, number, number] = [0, 1, 2].map((k) => a.amb[k]! + (b.amb[k]! - a.amb[k]!) * t) as [number, number, number];
  const lum = (amb[0] + amb[1] + amb[2]) / 3;
  const night = Math.max(0, Math.min(1, (0.95 - lum) / 0.45));
  const golden = hour > 16.5 && hour < 20 ? Math.max(0, 1 - Math.abs(hour - 18.4) / 1.8) : hour > 5.5 && hour < 7.5 ? 0.5 - Math.abs(hour - 6.5) / 2 : 0;
  const label = hour < 5 || hour >= 21 ? "NIGHT" : hour < 8 ? "DAWN" : hour < 17 ? "DAY" : "DUSK";
  return { hour, amb, night, golden: Math.max(0, golden), label };
}

/** The four parts of the day that change what walks in the grass. Same bounds as the HUD label. */
export type DayPeriod = "dawn" | "day" | "dusk" | "night";
export const PERIODS: readonly DayPeriod[] = ["dawn", "day", "dusk", "night"];
const PERIOD_START: Record<DayPeriod, number> = { dawn: 5, day: 8, dusk: 17, night: 21 };

export function periodOf(hour: number): DayPeriod {
  return hour < 5 || hour >= 21 ? "night" : hour < 8 ? "dawn" : hour < 17 ? "day" : "dusk";
}

/** Clock hour of a save: play time plus any time skipped by resting at kettles. */
export function worldHour(state: { playMs: number; clockShift?: number }, override?: string | null): number {
  return hourFor(state.playMs + (state.clockShift ?? 0), override);
}

/** Milliseconds of clock to skip so the next part of the day begins (resting at a kettle). */
export function skipToNextPeriod(hour: number): { ms: number; period: DayPeriod } {
  const next = PERIODS[(PERIODS.indexOf(periodOf(hour)) + 1) % PERIODS.length]!;
  const hours = (PERIOD_START[next] - hour + 24) % 24 || 24;
  return { ms: Math.round((hours / 24) * DAY_MS), period: next };
}

/** Clock label, e.g. "9:40". */
export function clockLabel(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.floor((hour - h) * 6) * 10;
  return `${h}:${m.toString().padStart(2, "0")}`;
}

let cloudTex: Float32Array | null = null;
function clouds(): Float32Array {
  if (!cloudTex) {
    cloudTex = new Float32Array(128 * 128);
    for (let y = 0; y < 128; y++) {
      for (let x = 0; x < 128; x++) {
        // Tileable by sampling a torus-mapped fbm.
        const a = (x / 128) * Math.PI * 2, b = (y / 128) * Math.PI * 2;
        const v = fbm(Math.cos(a) * 1.6 + 8, Math.sin(a) * 1.6 + Math.cos(b) * 1.6 + 8, 3, 71) * 0.5
          + fbm(Math.sin(b) * 1.6 + 3, Math.cos(a + b) * 1.2 + 3, 3, 72) * 0.5;
        cloudTex[y * 128 + x] = Math.max(0, Math.min(1, (v - 0.5) * 5));
      }
    }
  }
  return cloudTex;
}

export interface LightSource {
  x: number; // screen px
  y: number;
  r: number;
  color: Col;
  k: number;
}

const lightBuf = new Float32Array(SCREEN_W * SCREEN_H * 3);

/**
 * Grade the world view: ambient day/night, local lights (multiplicative so lit areas keep their
 * true colours), drifting cloud shadows, golden-hour warmth and a soft vignette.
 */
export function gradeWorld(g: Gfx, dl: DayLight, lights: LightSource[], camX: number, camY: number, t: number, reduced: boolean, h = SCREEN_H): void {
  const W = SCREEN_W;
  const buf = lightBuf;
  const [ar, ag, ab] = dl.amb;
  for (let i = 0, n = W * h; i < n; i++) {
    buf[i * 3] = ar;
    buf[i * 3 + 1] = ag;
    buf[i * 3 + 2] = ab;
  }
  for (const L of lights) {
    const r = L.r;
    const x0 = Math.max(0, Math.floor(L.x - r)), x1 = Math.min(W - 1, Math.ceil(L.x + r));
    const y0 = Math.max(0, Math.floor(L.y - r)), y1 = Math.min(h - 1, Math.ceil(L.y + r));
    const cr = (L.color[0] / 255) * L.k, cg = (L.color[1] / 255) * L.k, cb = (L.color[2] / 255) * L.k;
    for (let y = y0; y <= y1; y++) {
      const dy = (y - L.y) / r;
      for (let x = x0; x <= x1; x++) {
        const dx = (x - L.x) / r;
        const d2 = dx * dx + dy * dy;
        if (d2 >= 1) continue;
        const f = (1 - d2) * (1 - d2);
        const o = (y * W + x) * 3;
        buf[o] = buf[o]! + cr * f;
        buf[o + 1] = buf[o + 1]! + cg * f;
        buf[o + 2] = buf[o + 2]! + cb * f;
      }
    }
  }
  const cl = clouds();
  const drift = reduced ? 0 : t * 0.006;
  const cloudK = 0.13 * (1 - dl.night);
  const gold = dl.golden * 0.14;
  const d = g.img.data;
  const cxw = W / 2, cyw = h / 2;
  for (let y = 0; y < h; y++) {
    const wy = ((((y + camY) * 0.5 + drift * 0.6) | 0) & 127) * 128;
    const vy = (y - cyw) / cyw;
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      const o = i * 4;
      let lr = buf[i * 3]!, lg = buf[i * 3 + 1]!, lb = buf[i * 3 + 2]!;
      if (cloudK > 0) {
        const c = cl[wy + ((((x + camX) * 0.5 + drift) | 0) & 127)]!;
        const k = 1 - c * cloudK;
        lr *= k; lg *= k; lb *= k * 1.02;
      }
      const vx = (x - cxw) / cxw;
      const v = 1 - Math.max(0, vx * vx * 0.5 + vy * vy * 0.6 - 0.18) * 0.32;
      let r = d[o]! * lr * v, gg = d[o + 1]! * lg * v, b = d[o + 2]! * lb * v;
      if (gold > 0) {
        const lum = (r + gg + b) / 765;
        r += 255 * gold * lum;
        gg += 120 * gold * lum;
      }
      d[o] = r;
      d[o + 1] = gg;
      d[o + 2] = b;
    }
  }
}

// ---- Particles ------------------------------------------------------------------------------

export type PKind = "dust" | "leaf" | "pollen" | "firefly" | "steam" | "smoke" | "spark" | "ripple" | "petal" | "butterfly" | "snow" | "ember" | "glint";

export interface Particle {
  kind: PKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  seed: number;
  color?: Col;
}

export class Particles {
  list: Particle[] = [];

  spawn(p: Omit<Particle, "life" | "seed"> & { life?: number; seed?: number }): void {
    if (this.list.length > 360) this.list.shift();
    this.list.push({ life: p.life ?? p.max, seed: p.seed ?? Math.random(), ...p } as Particle);
  }

  update(dt: number): void {
    const s = dt / 1000;
    for (const p of this.list) {
      p.life -= dt;
      switch (p.kind) {
        case "firefly":
          p.vx += Math.sin((p.life + p.seed * 1000) / 300) * 4 * s;
          p.vy += Math.cos((p.life + p.seed * 900) / 360) * 4 * s;
          break;
        case "butterfly":
          p.vx += Math.sin((p.life + p.seed * 1000) / 200) * 30 * s;
          p.vy += Math.cos((p.life + p.seed * 700) / 260) * 24 * s;
          p.vx *= 0.98; p.vy *= 0.98;
          break;
        case "leaf":
        case "petal":
          p.vx += Math.sin((p.life + p.seed * 1000) / 180) * 18 * s;
          p.vy += 6 * s;
          break;
        case "steam":
        case "smoke":
          p.vx += 3 * s;
          break;
        case "dust":
          p.vx *= 0.92; p.vy *= 0.92;
          break;
        case "spark":
          p.vy += 60 * s;
          break;
        case "snow":
          p.vx = Math.sin((p.life + p.seed * 1000) / 400) * 8;
          break;
        case "ember":
          p.vx += Math.sin((p.life + p.seed * 800) / 220) * 20 * s;
          p.vy -= 4 * s;
          break;
      }
      p.x += p.vx * s;
      p.y += p.vy * s;
    }
    this.list = this.list.filter((p) => p.life > 0);
  }

  /** Draw in world space with the camera offset. */
  draw(g: Gfx, camX: number, camY: number, night: number): void {
    for (const p of this.list) {
      const x = Math.round(p.x - camX), y = Math.round(p.y - camY);
      if (x < -8 || y < -8 || x > SCREEN_W + 8 || y > SCREEN_H + 8) continue;
      const t = p.life / p.max;
      const fade = Math.min(1, t * 3, (1 - t) * 6);
      switch (p.kind) {
        case "dust":
          g.p(x, y, [236, 220, 190, 150 * fade]);
          if (t > 0.5) g.p(x + 1, y, [236, 220, 190, 110 * fade]);
          break;
        case "leaf":
          g.p(x, y, p.color ?? [120, 200, 90, 255 * fade]);
          g.p(x + (Math.sin(p.life / 90) > 0 ? 1 : -1), y, [70, 150, 70, 220 * fade]);
          break;
        case "petal":
          g.p(x, y, p.color ?? [255, 180, 205, 240 * fade]);
          break;
        case "pollen":
          g.p(x, y, [255, 250, 210, 170 * fade * (1 - night)]);
          break;
        case "firefly": {
          const pulse = 0.5 + 0.5 * Math.sin(p.life / 160 + p.seed * 20);
          const a = fade * night * pulse;
          if (a <= 0.02) break;
          g.glow(x + 0.5, y + 0.5, 5, [255, 240, 140, 255], 0.5 * a);
          g.p(x, y, [255, 255, 200, 255 * a]);
          break;
        }
        case "butterfly": {
          const flap = Math.floor(p.life / 90) % 2;
          const c = p.color ?? [255, 230, 120, 255];
          const a: Col = [c[0], c[1], c[2], 255 * fade * (1 - night)];
          g.p(x, y, a);
          if (flap) { g.p(x - 1, y - 1, a); g.p(x + 1, y - 1, a); } else { g.p(x - 1, y, a); g.p(x + 1, y, a); }
          break;
        }
        case "steam":
        case "smoke": {
          const r = (1 - t) * (p.kind === "smoke" ? 4 : 3) + 1;
          const c: Col = p.kind === "smoke" ? [200, 196, 210, 90 * fade] : [250, 250, 255, 110 * fade];
          g.disc(x, y, r, c);
          break;
        }
        case "spark":
          g.p(x, y, p.color ?? [255, 220, 120, 255 * fade]);
          break;
        case "snow":
          g.p(x, y, [255, 255, 255, 230 * fade]);
          if (p.seed > 0.6) g.p(x + 1, y, [220, 236, 255, 160 * fade]);
          break;
        case "ember": {
          const c: Col = p.seed > 0.5 ? [255, 210, 110, 255] : [255, 130, 60, 255];
          g.p(x, y, [c[0], c[1], c[2], 255 * fade]);
          if (night > 0.2) g.glow(x + 0.5, y + 0.5, 3, c, 0.35 * fade);
          break;
        }
        case "glint": {
          const tw = Math.abs(Math.sin(p.life / 120 + p.seed * 9));
          const c: Col = p.color ?? [220, 250, 255, 255];
          g.p(x, y, [c[0], c[1], c[2], 255 * fade * tw]);
          if (tw > 0.7) { g.p(x - 1, y, [c[0], c[1], c[2], 120 * fade]); g.p(x + 1, y, [c[0], c[1], c[2], 120 * fade]); g.p(x, y - 1, [c[0], c[1], c[2], 120 * fade]); g.p(x, y + 1, [c[0], c[1], c[2], 120 * fade]); }
          break;
        }
        case "ripple": {
          const r = (1 - t) * 6 + 1;
          g.ring(x, y, r, r * 0.45, [230, 250, 255, 110 * fade], 1);
          break;
        }
      }
    }
  }
}

/** Deterministic-ish helper for ambient spawning. */
export function chance(seed: number, t: number, p: number): boolean {
  return hash2(seed, Math.floor(t), 7) < p;
}
